import {observer} from "mobx-react";
import {Box, Button, Modal, Stack, Typography} from "@mui/material";
import styled from "@emotion/styled";
import {useViewController} from "data/services/locator";
import {Bindings} from "data/constants/bindings";
import CloseIcon from "@mui/icons-material/Close";
import {ButtonPrimaryInvert} from "views/components/buttons/buttons.component";
import React from "react";
import {Exist} from "views/components/exist/exist.component";
import {Preloader} from "views/components/preloader";
import {ArrowDownwardRounded} from "@mui/icons-material";
import {IModalShareController} from "views/components/modals/modal_share/modal_share.controller";
import {Sharing} from "views/components/sharing/sharing.component";
import {ShareType} from "data/enums";

const ModalContent = styled.div`
	position: fixed;
	top: 50%;
	left: 50%;
	transform: translate(-50%, -50%);
	display: flex;
	flex-direction: column;
	justify-content: flex-start;
	align-items: center;
	gap: 12px;
	outline: none;
	max-width: 600px;
	width: 100%;
	background: #fff;
	padding: 40px 20px;
	border-radius: 24px;
	text-align: center;
	color: #070a0d;
	font-family: var(--fontFamilyBase);

	@media screen and (max-width: 640px) {
		max-width: unset;
		left: 24px;
		right: 24px;
		width: auto;
		transform: translateY(-50%);
	}

	h3 {
		color: #070a0d;
		text-align: center;
		font-size: 19px;
		font-style: normal;
		font-weight: 600;
		line-height: 120%; /* 22.8px */
	}

	p {
		padding: 0 24px 12px;
	}

	b {
		font-weight: bold;
	}
`;

const CloseBlock = styled.div`
	width: 40px;
	height: 40px;
	display: flex;
	justify-content: flex-end;
	align-items: center;
	position: absolute;
	top: 12px;
	right: 12px;

	button {
		color: #14191f;
		border: 1px solid #14191f;
	}
`;

const ButtonFirstRow = styled.div`
	width: 100%;
	display: flex;
	flex-flow: column;
	gap: 16px;
	@media (max-width: 470px) {
		flex-flow: column;
		button {
			max-width: 300px;
			min-width: 0;
		}
	}
`;

const ShareImage = styled.img`
	max-width: 280px;
	opacity: 0;
	display: block;
	transform: scale(0) translateY(0);
	animation: zoomUp 0.6s ease-out forwards 0.3s;
	width: 100%;
`;

export const ModalShare: React.FC = observer(() => {
	const {
		isOpen,
		modalContent,
		i18n,
		playerFullName,
		close,
		imageUrl,
		isLoading,
		downloadImage,
		contestId,
	} = useViewController<IModalShareController>(Bindings.ModalShareController);

	return (
		<Modal open={isOpen}>
			<ModalContent>
				<CloseBlock>
					<Button sx={{minWidth: 0}} onClick={close}>
						<CloseIcon />
					</Button>
				</CloseBlock>
				{/*<Icon>*/}
				{/*	<ErrorIcon/>*/}
				{/*</Icon>*/}
				<Typography variant="h3">
					{i18n.t(
						modalContent?.title ?? "modal.winner_share.title",
						"Share the player of the game!"
					)}
				</Typography>

				<Stack
					sx={{
						borderRadius: "8px",
						width: "100%",
						padding: "16px 20px",
						gap: "16px",
					}}>
					<Box
						sx={{
							minHeight: "300px",
							width: "100%",
							display: "flex",
							justifyContent: "center",
							alignItems: "center",
							transition: "all 0.2s ease-in-out",
						}}>
						<Exist when={isLoading}>
							<Preloader />
						</Exist>
						<Exist when={!isLoading}>
							{imageUrl ? (
								<ShareImage
									src={imageUrl}
									loading="lazy"
									alt={`${playerFullName}`}
								/>
							) : null}
						</Exist>
					</Box>
					<ButtonFirstRow>
						<ButtonPrimaryInvert onClick={downloadImage} fullWidth={true}>
							<span style={{marginRight: 8, display: "flex", alignItems: "center"}}>
								{i18n.t("modal.winner_share.download_image", "Download Image")}
							</span>
							<ArrowDownwardRounded />
						</ButtonPrimaryInvert>

						<Sharing
							buttonText={i18n.t("modal.winner_share.share_button", "Share")}
							imageUrl={imageUrl}
							shareType={ShareType.Winner}
							contestId={contestId}
							shareMessage={i18n.t("modal.winner_share.share", ``)}
						/>
					</ButtonFirstRow>
				</Stack>
			</ModalContent>
		</Modal>
	);
});
